import supabase from '../supabase/supabase';
import { getOrders } from './adminWorkspaceService';

const proofTable = 'zevi_payment_proofs_20260925';

export async function getOrdersWithProofs() {
  const orders = await getOrders();
  const paymentIds = orders.map((order) => order.payment?.[0]?.id).filter(Boolean);
  if (!paymentIds.length) return orders;

  const { data, error } = await supabase
    .from(proofTable)
    .select('id,payment_id,original_filename,upload_status,created_at')
    .in('payment_id', paymentIds)
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error('Payment proofs could not be loaded. Verify the proof metadata table and admin read policy before reviewing payments.');
  }

  return orders.map((order) => ({
    ...order,
    payment: (order.payment || []).map((payment) => ({
      ...payment,
      proofs: (data || []).filter((proof) => proof.payment_id === payment.id)
    }))
  }));
}

export async function getInventoryWithColors() {
  const { data: inventory, error } = await supabase
    .from('zevi_inventory_20260925')
    .select('id,product_id,variant_id,quantity_on_hand,quantity_reserved,quantity_committed,low_stock_threshold,updated_at')
    .order('updated_at', { ascending: false });
  if (error) throw new Error('Inventory could not be loaded.');

  const productIds = [...new Set((inventory || []).map((row) => row.product_id).filter(Boolean))];
  const variantIds = [...new Set((inventory || []).map((row) => row.variant_id).filter(Boolean))];
  const [productsResult, variantsResult] = await Promise.all([
    productIds.length
      ? supabase.from('zevi_products_20260925').select('id,name').in('id', productIds)
      : Promise.resolve({ data: [], error: null }),
    variantIds.length
      ? supabase.from('zevi_product_variants_20260925').select('id,name,color,attributes').in('id', variantIds)
      : Promise.resolve({ data: [], error: null })
  ]);
  if (productsResult.error || variantsResult.error) {
    throw new Error('Product or color labels could not be loaded.');
  }

  const products = new Map((productsResult.data || []).map((row) => [row.id, row]));
  const variants = new Map((variantsResult.data || []).map((row) => [row.id, row]));
  return (inventory || []).map((row) => ({
    ...row,
    product: products.get(row.product_id),
    variant: variants.get(row.variant_id)
  }));
}

async function countOrders(query) {
  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

export async function getAccurateAdminMetrics(start, end) {
  const table = 'zevi_orders_20260925';
  const range = (query) =>
    query.gte('created_at', start.toISOString()).lt('created_at', end.toISOString());
  const count = (configure = (query) => query) =>
    countOrders(range(configure(supabase.from(table).select('id', { count: 'exact', head: true }))));

  const [totalOrders, awaitingPayment, awaitingConfirmation, processing, readyToShip, delivered, cancelled] = await Promise.all([
    count(),
    count((query) => query.in('payment_status', ['proof_submitted', 'under_review'])),
    count((query) => query.eq('order_status', 'order_received')),
    count((query) => query.in('order_status', ['confirmed', 'processing'])),
    count((query) => query.in('order_status', ['packed', 'shipped'])),
    count((query) => query.eq('order_status', 'delivered')),
    count((query) => query.in('order_status', ['cancelled', 'rejected', 'refunded']))
  ]);

  let revenue = 0;
  let codUnpaid = 0;
  for (let offset = 0; ; offset += 500) {
    const { data, error } = await range(
      supabase.from(table)
        .select('id,grand_total,order_status,payment_status,payment_method')
    ).order('id').range(offset, offset + 499);
    if (error) throw new Error('Dashboard revenue could not be loaded.');
    for (const order of data || []) {
      if (['cancelled', 'rejected', 'refunded'].includes(order.order_status)) continue;
      if (['verified', 'paid'].includes(order.payment_status)) {
        revenue += Number(order.grand_total) || 0;
      }
      if (order.payment_method === 'cash_on_delivery' && order.payment_status === 'unpaid') {
        codUnpaid += Number(order.grand_total) || 0;
      }
    }
    if ((data || []).length < 500) break;
  }

  return {
    totalOrders,
    awaitingPayment,
    awaitingConfirmation,
    processing,
    readyToShip,
    delivered,
    cancelled,
    revenue,
    codUnpaid
  };
}